
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { environment } from '@environment/environment';
import { feedbackEnvironment } from '@feedback/environment/environment.feedback';
import { CookieConsentService } from '@shared/services/cookie-consent.service';

@Injectable({
  providedIn: 'root'
})
export class EnvironmentsService {
  public ENDPOINTS_ASSETS: string = environment.ENDPOINTS.ASSETS;
  public logo = String(
    `${this.ENDPOINTS_ASSETS}/images/vitay.io/logo-turquoise.svg`
  );

  private _isGtmInjected: boolean = Boolean(false);

  constructor(
    @Inject(DOCUMENT) private readonly _document: any,
    private readonly _cookieConsent: CookieConsentService
  ) {}

  public setScripts() {
    if (environment.IS_PRODUCTION) {
      // Google Consent Mode v2: everything denied until the user opts in.
      this._gtag('consent', 'default', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied'
      });

      this._cookieConsent.consent$.subscribe(() => {
        const analytics = this._cookieConsent.isGranted('analytics');
        const advertisement = this._cookieConsent.isGranted('advertisement');

        this._gtag('consent', 'update', {
          ad_storage: advertisement ? 'granted' : 'denied',
          ad_user_data: advertisement ? 'granted' : 'denied',
          ad_personalization: advertisement ? 'granted' : 'denied',
          analytics_storage: analytics ? 'granted' : 'denied'
        });

        if ((analytics || advertisement) && !this._isGtmInjected) {
          this._injectGtm();
        }
      });

      this._cookieConsent
        .grantedOnce$('functional')
        .subscribe(() => this._injectHelpScout());
    }
  }

  // gtag() must push the `arguments` object itself, not an array.
  private _gtag(..._args: any[]): void {
    const win = this._document.defaultView as any;
    win.dataLayer = win.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    win.dataLayer.push(arguments);
  }

  private _injectGtm(): void {
    this._isGtmInjected = true;

    const head = this._document.getElementsByTagName('head')[0];
    const body = this._document.getElementsByTagName('body')[0];

    const scriptGATag = this._document.createElement('script');
    scriptGATag.type = 'text/javascript';

    let gaTagInnerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});`;
    gaTagInnerHTML += `var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;`;
    gaTagInnerHTML += `j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})`;
    gaTagInnerHTML += `(window, document, 'script', 'dataLayer', '${feedbackEnvironment.GOOGLE_ANALYTICS.GTM}');`;

    scriptGATag.innerHTML = gaTagInnerHTML;

    const noScriptGATag = this._document.createElement('noscript');
    noScriptGATag.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${feedbackEnvironment.GOOGLE_ANALYTICS.GTM}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;

    body.appendChild(noScriptGATag);
    head.appendChild(scriptGATag);
  }

  private _injectHelpScout(): void {
    const head = this._document.getElementsByTagName('head')[0];

    const script = this._document.createElement('script');
    script.type = 'text/javascript';

    let helpScoutTag = `!function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){});`;
    helpScoutTag += `window.Beacon('init', 'fc8b4ae7-983d-40c6-be18-529973438445')`;

    script.innerHTML = helpScoutTag;
    head.appendChild(script);
  }
}
